import React, { type CSSProperties, useEffect, useMemo, useState } from 'react'
import type { MessageDescriptor } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LuxPlusMemberSavingCard from './LuxPlusMemberSavingCard'

export interface MemberSaving {
  id: number
  destination: MessageDescriptor
  nights: number
  savedAmountAud: number
  imageId: string
}

const MEMBER_SAVINGS: ReadonlyArray<MemberSaving> = [
  {
    id: 1,
    destination: { id: 'rc2VULQfpKXsPcyYylkE', defaultMessage: 'Fiji' },
    nights: 7,
    savedAmountAud: 5590,
    imageId: 'h1t0c6c9brq83bwhazx',
  },
  {
    id: 2,
    destination: { id: 'C8i0AtuM_moGzcvh8XxK', defaultMessage: 'Maldives' },
    nights: 7,
    savedAmountAud: 2205,
    imageId: 'dapbr82l65bcj5iy6v8',
  },
  {
    id: 3,
    destination: { id: 'fDhjl0IdrXseMvLYXUbT', defaultMessage: 'Egypt' },
    nights: 16,
    savedAmountAud: 2080,
    imageId: 'm4rcv17j80xrikxq419',
  },
  {
    id: 4,
    destination: { id: 'XUZbdROHp-nOPUzKggJm', defaultMessage: 'Indonesia' },
    nights: 7,
    savedAmountAud: 2057,
    imageId: 'vg4uvt2y5dgkivujuba6',
  },
  {
    id: 5,
    destination: { id: 'cOdkwyuz2KWTem04pJ5e', defaultMessage: 'Czech Republic' },
    nights: 24,
    savedAmountAud: 2000,
    imageId: 'wbk1yjnf56awezs1lu',
  },
  {
    id: 6,
    destination: { id: 'QHW72X6FljASRSC67SZj', defaultMessage: 'Vietnam' },
    nights: 13,
    savedAmountAud: 1799,
    imageId: '7pmj8f3m1cz7ip3920k',
  },
  {
    id: 7,
    destination: { id: 'Jbi1718NPqQ0cRhxeO2p', defaultMessage: 'Japan' },
    nights: 13,
    savedAmountAud: 1700,
    imageId: '6tkmq2mo1izznyjyfln',
  },
  {
    id: 8,
    destination: { id: 'G5lbYPfGeE3zA2pdoSYx', defaultMessage: 'Thailand' },
    nights: 10,
    savedAmountAud: 1353,
    imageId: 'lids8h5rs9zj2xvkxr',
  },
  {
    id: 9,
    destination: { id: 'Y_dKkI4UeuFezabUIGEe', defaultMessage: 'United Kingdom' },
    nights: 20,
    savedAmountAud: 1160,
    imageId: 'bh52x5crz46nt54uxsl',
  },
  {
    id: 10,
    destination: { id: 'As0YVcqY01DfzYIpb6t3', defaultMessage: 'South Africa' },
    nights: 8,
    savedAmountAud: 1200,
    imageId: 'v2u8uob7mgdu7o6y6njq',
  },
]

interface VisibleSaving {
  saving: MemberSaving
  slot: number
  top: number
  opacity: number
  scale: number
}

const SHADOW_PADDING = 24
const TRANSITION_DURATION_MS = 800
const CYCLE_INTERVAL_MS = 5000

const VISIBLE_COUNT_DESKTOP = 3
const VISIBLE_COUNT_MOBILE = 2

const CARD_GAP = 9
const CARD_INNER_PADDING = 16
const CARD_CONTENT_GAP = 16
const CARD_BORDER_WIDTH = 1

const MOBILE_CARD_HEIGHT = 150
const DESKTOP_IMAGE_SIZE = 103

const MOBILE_BACK_SCALE = 0.85
const MOBILE_PEEK = (CARD_INNER_PADDING - 1) * MOBILE_BACK_SCALE

const CardStack = styled.div`
  position: relative;
  overflow-y: clip;
  padding: ${rem(SHADOW_PADDING)} 0;
  margin: ${rem(-SHADOW_PADDING)} auto;
  text-align: start;
  max-width: ${rem(590)};
`

const StackItem = styled.div`
  position: absolute;
  width: 100%;
  transform-origin: top center;
  transition:
    top ${TRANSITION_DURATION_MS}ms ease,
    opacity ${TRANSITION_DURATION_MS}ms ease,
    transform ${TRANSITION_DURATION_MS}ms ease;
`

const CardRoot = styled(Sheet)`
  display: flex;
  align-items: center;
  height: ${rem(170)};

  // Specialised breakpoint between very small and mobile
  @media (min-width: ${rem(301)}) {
    height: ${rem(130)};
  }

  ${mediaQueryUp.tablet} {
    height: auto;
  }
`

function getDesktopSlotStyle(slot: number): { opacity: number; scale: number } {
  if (slot <= -1) return { opacity: 0, scale: 1 }
  if (slot === 0) return { opacity: 1, scale: 1 }
  if (slot === 1) return { opacity: 0.7, scale: 0.95 }
  if (slot === 2) return { opacity: 0.4, scale: 0.9 }
  return { opacity: 0, scale: 0.9 }
}

function getMobileSlotStyle(slot: number): { opacity: number; scale: number } {
  if (slot <= -1) return { opacity: 0, scale: 1 }
  if (slot === 0) return { opacity: 1, scale: 1 }
  if (slot === 1) return { opacity: 0.5, scale: MOBILE_BACK_SCALE }
  return { opacity: 0, scale: MOBILE_BACK_SCALE }
}

function useMemberSavingCycle(
  savings: ReadonlyArray<MemberSaving>,
  type: 'compact' | 'expanded',
): {
  visibleSavings: Array<VisibleSaving>
  stackStyle: CSSProperties
} {
  const isCompact = type === 'compact'
  const visibleCount = isCompact ? VISIBLE_COUNT_MOBILE : VISIBLE_COUNT_DESKTOP
  const cardHeight = isCompact
    ? MOBILE_CARD_HEIGHT
    : 2 * CARD_INNER_PADDING + DESKTOP_IMAGE_SIZE + 2 * CARD_BORDER_WIDTH
  const getSlotStyle = isCompact ? getMobileSlotStyle : getDesktopSlotStyle

  const [baseIndex, setBaseIndex] = useState(0)
  const [phase, setPhase] = useState<'idle' | 'entering' | 'shifting'>('idle')
  const count = savings.length

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((prev) => prev === 'idle' ? 'entering' : prev)
    }, CYCLE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (phase !== 'entering') return
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('shifting'))
    })
    return () => cancelAnimationFrame(raf)
  }, [phase])

  useEffect(() => {
    if (phase !== 'shifting') return
    const timeout = setTimeout(() => {
      setBaseIndex((prev) => (prev - 1 + count) % count)
      setPhase('idle')
    }, TRANSITION_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [phase, count])

  const visibleSavings = useMemo<Array<VisibleSaving>>(() => {
    if (isCompact) {
      const backTop = SHADOW_PADDING + cardHeight * (1 - MOBILE_BACK_SCALE) + MOBILE_PEEK - CARD_BORDER_WIDTH

      function topForSlot(slot: number): number {
        if (slot <= -1) return SHADOW_PADDING - cardHeight - CARD_GAP
        if (slot === 0) return SHADOW_PADDING
        if (slot === 1) return backTop
        return backTop + cardHeight * MOBILE_BACK_SCALE
      }

      if (phase === 'idle') {
        return Array.from({ length: visibleCount }, (_, i) => {
          const saving = savings[(baseIndex + i) % count]
          const { opacity, scale } = getSlotStyle(i)
          return { saving, slot: i, top: topForSlot(i), opacity, scale }
        })
      }

      const enteringIndex = (baseIndex - 1 + count) % count
      const indices = [enteringIndex, baseIndex, (baseIndex + 1) % count]

      return indices.map((savingIdx, i) => {
        const slot = phase === 'entering' ? i - 1 : i
        const saving = savings[savingIdx]
        const { opacity, scale } = getSlotStyle(slot)
        return { saving, slot, top: topForSlot(slot), opacity, scale }
      })
    }

    if (phase === 'idle') {
      const cards: Array<VisibleSaving> = []
      let top = SHADOW_PADDING
      for (let i = 0; i < visibleCount; i++) {
        const saving = savings[(baseIndex + i) % count]
        const { opacity, scale } = getSlotStyle(i)
        cards.push({ saving, slot: i, top, opacity, scale })
        top += cardHeight * scale + CARD_GAP
      }
      return cards
    }

    const enteringIndex = (baseIndex - 1 + count) % count
    const indices = [
      enteringIndex,
      baseIndex,
      (baseIndex + 1) % count,
      (baseIndex + 2) % count,
    ]

    let top = phase === 'entering'
      ? SHADOW_PADDING - cardHeight * getSlotStyle(-1).scale - CARD_GAP
      : SHADOW_PADDING

    return indices.map((savingIdx, i) => {
      const slot = phase === 'entering' ? i - 1 : i
      const saving = savings[savingIdx]
      const { opacity, scale } = getSlotStyle(slot)
      const card = { saving, slot, top, opacity, scale }
      top += cardHeight * scale + CARD_GAP
      return card
    })
  }, [savings, baseIndex, phase, cardHeight, count, isCompact, visibleCount, getSlotStyle])

  const stackStyle = useMemo<CSSProperties>(() => {
    if (isCompact) {
      return { height: rem(cardHeight + MOBILE_PEEK + 2 * SHADOW_PADDING) }
    }
    let h = SHADOW_PADDING
    for (let i = 0; i < visibleCount; i++) {
      const { scale } = getSlotStyle(i)
      h += cardHeight * scale
      if (i < visibleCount - 1) h += CARD_GAP
    }
    h += SHADOW_PADDING
    return { height: rem(h) }
  }, [cardHeight, isCompact, visibleCount, getSlotStyle])

  return { visibleSavings, stackStyle }
}

interface Props {
  type: 'compact' | 'expanded'
}

function LuxPlusMemberSavingsCarousel({ type }: Props) {
  const { visibleSavings, stackStyle } = useMemberSavingCycle(MEMBER_SAVINGS, type)
  const visibleCount = type === 'compact' ? VISIBLE_COUNT_MOBILE : VISIBLE_COUNT_DESKTOP

  return (
    <CardStack style={stackStyle}>
      {visibleSavings.map(({ saving, top, opacity, scale }, i) => (
        <StackItem
          key={saving.id}
          style={{
            top: rem(top),
            opacity,
            transform: `scale(${scale})`,
            zIndex: 1 + visibleCount - i,
          }}
        >
          <CardRoot background="normal-background" borderRadius="M" shadow="bottom-medium" padding={CARD_INNER_PADDING}>
            <Group direction="horizontal" gap={CARD_CONTENT_GAP} verticalAlign="center">
              <LuxPlusMemberSavingCard
                saving={saving}
              />
            </Group>
          </CardRoot>
        </StackItem>
      ))}
    </CardStack>
  )
}

export default LuxPlusMemberSavingsCarousel
